import { useMemo, useState } from 'react';
import { ReceiptTextIcon, XIcon } from 'lucide-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Combobox,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  SearchInput,
  SelectInput,
  SkeletonTable,
  StatTile,
  TrendChart,
  type DataTableSort,
  type TrendPoint,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  EXPENSES_PAGE_SIZE,
  EXPENSE_SORT_KEYS,
  PNL_CATEGORIES,
  listFinanceTransactions,
  readFinanceFacets,
  readFinanceSummary,
  setTransactionCategory,
  type FinanceTransaction,
  type PnlCategory,
  type SpendGroup,
} from '@/lib/finance';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';

const DAY_MS = 86_400_000;
const DEFAULT_WINDOW_DAYS = 365;
const BREAKDOWN_PAGE_SIZE = 10;

/** YYYY-MM-DD for a Date (UTC), the on-the-wire window format. */
function ymd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const intFmt = new Intl.NumberFormat('en-US');
const moneyFull = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function monthPoints(rows: readonly SpendGroup[]): TrendPoint[] {
  return rows.map((row) => ({ key: row.key, label: row.key, value: row.amount }));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * A category or vendor spend breakdown as a sortable, searchable, paginated
 * table. Sorting/paging/search are client-side over the full window list (the
 * facets call returns every group with its amount). Clicking a row drills the
 * whole page down to that key.
 */
function BreakdownTable({
  title,
  nounLabel,
  rows,
  onPick,
}: {
  readonly title: string;
  readonly nounLabel: string;
  readonly rows: readonly SpendGroup[];
  readonly onPick: (key: string) => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'amount', direction: 'desc' });

  const grandTotal = useMemo(() => rows.reduce((sum, row) => sum + row.amount, 0), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? rows.filter((row) => row.key.toLowerCase().includes(q)) : rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...base].sort((a, b) =>
      sort.key === 'name' ? a.key.localeCompare(b.key) * dir : (a.amount - b.amount) * dir,
    );
  }, [rows, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / BREAKDOWN_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * BREAKDOWN_PAGE_SIZE;
  const pageRows = filtered.slice(start, start + BREAKDOWN_PAGE_SIZE);

  function onSort(key: string): void {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' },
    );
    setPage(1);
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-portal-card-title font-bold text-portal-ink">
          {title} <span className="text-portal-small font-normal text-portal-muted">({intFmt.format(rows.length)})</span>
        </h3>
        <SearchInput
          label={`Search ${title.toLowerCase()}`}
          placeholder={`Search ${title.toLowerCase()}`}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-44"
        />
      </div>
      {pageRows.length === 0 ? (
        <Panel>
          <EmptyState title={`No ${title.toLowerCase()} match`} message="Try a different search." />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: nounLabel, sortKey: 'name' },
            { label: 'Spend', sortKey: 'amount' },
            'Share',
          ]}
          rightAlignedHeaders={[1, 2]}
          sort={sort}
          onSort={onSort}
          minWidth={360}
        >
          {pageRows.map((row) => {
            const share = grandTotal > 0 ? Math.round((row.amount / grandTotal) * 100) : 0;
            return (
              <tr key={row.key} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button type="button" onClick={() => onPick(row.key)} className="text-left text-brand hover:underline">
                    {row.key}
                  </button>
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold text-portal-ink">
                  {moneyFull.format(row.amount)}
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small text-portal-muted">{share}%</td>
              </tr>
            );
          })}
        </DataTable>
      )}
      {totalPages > 1 ? (
        <Pagination
          page={clampedPage}
          totalPages={totalPages}
          onChange={setPage}
          summary={`${start + 1}–${start + pageRows.length} of ${filtered.length}`}
        />
      ) : null}
    </div>
  );
}

/**
 * The expenses explorer: every mirrored QuickBooks expense line, filterable by
 * date, category and vendor and searchable. A four-stat summary and a
 * spend-over-time chart sit above the Categories and Vendors tables (sortable,
 * searchable, paginated) and the full transaction table. Clicking any category
 * or vendor drills the whole view down to it — its lines and how its spend
 * changed over time. Admin-only (view_analytics), enforced by the API and the
 * route guard.
 */
export function ExpensesPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(EXPENSE_SORT_KEYS as unknown as string[], { key: 'date', direction: 'desc' });

  const defaultTo = ymd(new Date());
  const defaultFrom = ymd(new Date(Date.now() - DEFAULT_WINDOW_DAYS * DAY_MS));
  const from = params.get('from') ?? defaultFrom;
  const to = params.get('to') ?? defaultTo;
  const category = params.get('category') ?? '';
  const vendor = params.get('vendor') ?? '';
  const pnl = params.get('pnl') ?? '';

  /** Sets or clears a filter param and returns to page 1. */
  function setFilter(key: string, value: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  const window = { from, to };
  const scope = {
    ...(category ? { category } : {}),
    ...(vendor ? { vendor } : {}),
    ...(pnl ? { pnl } : {}),
  };

  const facets = useQuery({
    queryKey: ['finance-facets', from, to],
    queryFn: ({ signal }) => readFinanceFacets(window, signal),
  });
  const summary = useQuery({
    queryKey: ['finance-summary', from, to, category, vendor, pnl],
    queryFn: ({ signal }) => readFinanceSummary({ ...window, ...scope }, signal),
    placeholderData: keepPreviousData,
  });
  const transactions = useQuery({
    queryKey: ['finance-transactions', from, to, category, vendor, pnl, debounced, sort, page],
    queryFn: ({ signal }) =>
      listFinanceTransactions(
        { ...window, ...scope, search: debounced, page, ...(sort ? { sort } : {}) },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  // Manual P&L categorization: sets the line (sticks) and learns the vendor rule.
  const categorize = useMutation({
    mutationFn: ({ lineId, category: cat }: { lineId: string; category: PnlCategory }) =>
      setTransactionCategory(lineId, cat),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['finance-pnl'] });
    },
  });

  // One line's P&L category picker: inline in the table, labeled on a phone card.
  const pnlSelect = (row: FinanceTransaction, id?: string): React.ReactElement => (
    <SelectInput
      density="compact"
      {...(id !== undefined ? { id } : { 'aria-label': `P&L category for ${row.vendor}` })}
      className={id !== undefined ? 'h-10' : 'min-w-[11rem]'}
      value={row.pnlCategory || ''}
      disabled={categorize.isPending && categorize.variables?.lineId === row.transactionLineId}
      title={row.pnlCategorySource === 'manual' ? 'Set manually' : 'Auto-categorized'}
      onChange={(event) => categorize.mutate({ lineId: row.transactionLineId, category: event.target.value as PnlCategory })}
    >
      {row.pnlCategory ? null : (
        <option value="" disabled>
          —
        </option>
      )}
      {PNL_CATEGORIES.map((c) => (
        <option key={c.key} value={c.key}>
          {c.label}
        </option>
      ))}
    </SelectInput>
  );

  const rows = transactions.data?.items ?? [];
  const pagination = transactions.data?.pagination;
  const spend = summary.data?.spend ?? 0;
  const scoped = category !== '' || vendor !== '' || pnl !== '';
  const categories = facets.data?.categories ?? [];
  const vendors = facets.data?.vendors ?? [];

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Expenses"
        subtitle="Every QuickBooks expense line — explore by category, vendor and date."
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <SearchInput
          label="Search expenses"
          placeholder="Vendor, category or memo"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-xs"
        />
        <div>
          <label htmlFor="exp-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            From
          </label>
          <DatePicker id="exp-from" value={from} placeholder="Start" onChange={(value) => setFilter('from', value)} />
        </div>
        <div>
          <label htmlFor="exp-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            To
          </label>
          <DatePicker id="exp-to" value={to} placeholder="End" onChange={(value) => setFilter('to', value)} />
        </div>
        <div className="w-52">
          <label htmlFor="exp-category" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            Category
          </label>
          <Combobox
            id="exp-category"
            options={categories.map((c) => ({ value: c.key, label: c.key }))}
            value={category}
            onChange={(value) => setFilter('category', value)}
            placeholder="All categories"
            noneLabel="All categories"
          />
        </div>
        <div className="w-52">
          <label htmlFor="exp-vendor" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            Vendor
          </label>
          <Combobox
            id="exp-vendor"
            options={vendors.map((v) => ({ value: v.key, label: v.key }))}
            value={vendor}
            onChange={(value) => setFilter('vendor', value)}
            placeholder="All vendors"
            noneLabel="All vendors"
          />
        </div>
        <div className="w-52">
          <label htmlFor="exp-pnl" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            P&amp;L category
          </label>
          <Combobox
            id="exp-pnl"
            options={PNL_CATEGORIES.map((c) => ({ value: c.key, label: c.label }))}
            value={pnl}
            onChange={(value) => setFilter('pnl', value)}
            placeholder="All P&L categories"
            noneLabel="All P&L categories"
          />
        </div>
      </div>

      {/* Active drill-down chips */}
      {scoped ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {category ? (
            <FilterChip label={`Category: ${category}`} onClear={() => setFilter('category', '')} />
          ) : null}
          {vendor ? <FilterChip label={`Vendor: ${vendor}`} onClear={() => setFilter('vendor', '')} /> : null}
          {pnl ? (
            <FilterChip
              label={`P&L: ${PNL_CATEGORIES.find((c) => c.key === pnl)?.label ?? pnl}`}
              onClear={() => setFilter('pnl', '')}
            />
          ) : null}
        </div>
      ) : null}

      {/* Four-stat summary */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile
          label={scoped ? 'Spend (this view)' : 'Total spend'}
          value={moneyFull.format(spend)}
          hint={`${from} → ${to}`}
        />
        <StatTile
          label="Expense lines"
          value={pagination ? intFmt.format(pagination.total) : '—'}
          hint={scoped ? 'matching filters' : 'in this window'}
        />
        <StatTile label="Categories" value={intFmt.format(categories.length)} hint="with spend" />
        <StatTile label="Vendors" value={intFmt.format(vendors.length)} hint="with spend" />
      </div>

      {/* Spend over time */}
      {summary.isError ? (
        <div className="mb-4">
          <ErrorNotice title="Spend summary unavailable" message={errorMessage(summary.error)} />
        </div>
      ) : summary.data && summary.data.byMonth.length > 0 ? (
        <Panel className="mb-4 p-5">
          <h3 className="text-portal-card-title font-bold text-portal-ink">Spend over time</h3>
          <TrendChart
            points={monthPoints(summary.data.byMonth)}
            title="Spend by month"
            unit="USD"
            variant="bar"
            height={240}
            formatValue={(v) => moneyCompact.format(v)}
          />
        </Panel>
      ) : null}

      {/* Categories + Vendors */}
      {facets.isError ? (
        <div className="mb-4">
          <ErrorNotice title="Breakdowns unavailable" message={errorMessage(facets.error)} />
        </div>
      ) : facets.isPending ? (
        <div className="mb-4 grid gap-4 lg:grid-cols-2">
          <SkeletonTable label="Loading categories…" />
          <SkeletonTable label="Loading vendors…" />
        </div>
      ) : (
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <BreakdownTable title="Categories" nounLabel="Category" rows={categories} onPick={(key) => setFilter('category', key)} />
          <BreakdownTable title="Vendors" nounLabel="Vendor" rows={vendors} onPick={(key) => setFilter('vendor', key)} />
        </div>
      )}

      {/* Transactions */}
      <h3 className="mb-2 text-portal-card-title font-bold text-portal-ink">Transactions</h3>
      {transactions.isError ? (
        <ErrorNotice title="Expenses unavailable" message={errorMessage(transactions.error)} />
      ) : transactions.isPending ? (
        <SkeletonTable label="Loading expenses…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<ReceiptTextIcon aria-hidden className="size-6" />}
            title="No expenses match"
            message="No expense lines fall in this window and filter. Widen the dates or clear a filter."
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Date', sortKey: 'date' },
              { label: 'Vendor', sortKey: 'vendor' },
              { label: 'Category', sortKey: 'category' },
              'P&L',
              'Memo',
              { label: 'Amount', sortKey: 'amount' },
            ]}
            rightAlignedHeaders={[5]}
            sort={sort}
            onSort={onSort}
            minWidth={960}
            cards={rows.map((row: FinanceTransaction) => ({
              key: row.transactionLineId,
              title: row.vendor,
              subtitle: formatDate(row.transactionDate),
              fields: [
                { label: 'Amount', value: moneyFull.format(row.amount) },
                { label: 'Category', value: row.category },
                ...(row.memo ? [{ label: 'Memo', value: row.memo }] : []),
              ],
              action: (
                <div className="py-2">
                  <label htmlFor={`pnl-${row.transactionLineId}`} className="text-portal-micro text-portal-muted">
                    P&amp;L category
                  </label>
                  {pnlSelect(row, `pnl-${row.transactionLineId}`)}
                </div>
              ),
            }))}
          >
            {rows.map((row: FinanceTransaction) => (
              <tr key={row.transactionLineId} className="border-t border-portal-line align-middle">
                <td className="w-[110px] p-3 text-portal-small whitespace-nowrap">{formatDate(row.transactionDate)}</td>
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    onClick={() => setFilter('vendor', row.vendor)}
                    className="text-left text-brand hover:underline"
                  >
                    {row.vendor}
                  </button>
                </td>
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    onClick={() => setFilter('category', row.category)}
                    className="text-left text-brand hover:underline"
                  >
                    {row.category}
                  </button>
                </td>
                <td className="p-3">{pnlSelect(row)}</td>
                <td className="p-3 text-portal-small text-portal-muted">{row.memo || '—'}</td>
                <td className="p-3 text-right text-portal-small font-bold whitespace-nowrap text-portal-ink">
                  {moneyFull.format(row.amount)}
                </td>
              </tr>
            ))}
          </DataTable>
          {pagination && pagination.totalPages > 1 ? (
            <Pagination
              page={page}
              totalPages={pagination.totalPages}
              onChange={setPage}
              summary={`${(page - 1) * EXPENSES_PAGE_SIZE + 1}–${(page - 1) * EXPENSES_PAGE_SIZE + rows.length} of ${pagination.total}`}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

/** A removable active-filter pill. */
function FilterChip({ label, onClear }: { readonly label: string; readonly onClear: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1 rounded-full border border-brand bg-brand-tint px-2.5 py-0.5 text-portal-micro font-semibold text-brand hover:bg-white"
      aria-label={`Clear filter ${label}`}
    >
      {label} <XIcon aria-hidden className="size-3" />
    </button>
  );
}
